import { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { Form, Button, Row, Col, Card, Spinner } from 'react-bootstrap'
import Flag from 'react-flagkit'
import * as OneMoneyMailMobileApi from 'omm-rest-api-client'
import debounce from 'lodash.debounce'
import '../../styles/components/CalculatorStyle.scss'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { getCalculatorContent } from '../../actions/cms'
import { productsGet, currenciesGet, setCurrentProduct, setTrxCalculatorData } from '../../actions/transaction'
import validation from './validationSchema'
import { usePrevious, getInputLabel, getInputsSchema } from '../../utils'
import currencyFormatterGBP from '../../utils/currencyFormatterGBP'
import { getPath } from '../../MultilingualRouter/MultilingualRouter'
import { ProductsEnum } from '../../pages/TransferPage/components/ProductsEnum'
import Pages from '../../Enum/Pages'
import axiosInstance, { apiUrl } from '../../config/api'
import apiErrorHandler from '../../utils/apiErrorHandler'
import formatExchangeRate from '../../utils/formatExchangeRate'
import CurrencyEnum from '../../Enum/CurrencyEnum'
import config from '../../config/cms'
import ProductPicker from './ProductPicker'
import AnalyticsEventEnum from '../../Enum/AnalyticsEventEnum'
import SendAnalyticsEvent from '../../utils/SendAnalyticsEvent'

const apiInstance = new OneMoneyMailMobileApi.TransactionsApi({}, apiUrl, axiosInstance)
const calculatorPost = debounce(
  (
    lang,
    currentProductId,
    value,
    setCalculatorData,
    setValue,
    setLoading,
    dispatch,
    lastEdited, // true for payInAmount,
    history
  ) => {
    const handleResponse = (response) => {
      const newData = response.data?.data?.attributes
      dispatch(setTrxCalculatorData(newData))
      dispatch(setCurrentProduct(currentProductId))
      setCalculatorData(newData)
      setValue('payoutValue', parseFloat(newData?.payoutAmount).toFixed(2))
      setValue('transactionValue', parseFloat(newData?.sendingAmount).toFixed(2))
      setValue('charge', newData.charge)
    }

    const postData = {
      type: 'products',
      attributes: {
        productId: currentProductId,
        clientType: 'ppi3',
        currencyIn: 'GBP',
        currencyOut: 'PLN'
      }
    }

    if (!lastEdited) {
      postData.attributes.amount = parseFloat(value).toFixed(2)
    } else {
      postData.attributes.receiveAmount = parseFloat(value).toFixed(2)
      postData.attributes.amount = 0
    }

    apiInstance
      .productsCalculatorPost(lang, { data: postData })
      .then((response) => handleResponse(response))
      .catch((err) => {
        apiErrorHandler(err, dispatch, history)
      })
      .finally(() => setLoading(false))
  },
  1500
)

const Calculator = ({
  pageProductId = ProductsEnum.Economic
}) => {
  const content = useSelector((state) => state.cms?.calculator)
  const schema = validation(getInputsSchema(content.inputs) || [])
  const {
    control,
    trigger,
    watch,
    setValue,
    formState: { errors },
    handleSubmit
  } = useForm({
    resolver: yupResolver(schema)
  })

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const isMounted = useRef(false)
  const location = useLocation()
  const stateProductID = useSelector((state) => state.transaction.currentProduct)
  const [defaultProductId, setDefaultProductId] = useState(stateProductID)
  const lang = useSelector((state) => state.lang.lang)
  const [calculatorData, setCalculatorData] = useState({})
  const [loading, setLoading] = useState(false)
  const currentProductId = Number(watch('productId'))
  const payInAmount = watch('transactionValue')
  const previousProductId = usePrevious(defaultProductId)
  const products = useSelector((state) => state.transaction.products)
  const currencyIn = useSelector((state) => state.transaction?.currenciesIn)
  const currencyOut = useSelector((state) => state.transaction?.currenciesOut)
  const prevLang = usePrevious(lang)
  const [currentProduct, setCurrentProductItem] = useState(false)
  const calculatorRef = useRef()
  const [productPagePath, setProductPagePath] = useState(Pages.transferStandardPage)
  const isLogin = useSelector((state) => state.customerAccount?.customer?.id)?.length > 0

  useEffect(() => {
    if (currentProductId === ProductsEnum.Economic) {
      setProductPagePath(Pages.transferStandardPage)
    }
    if (currentProductId === ProductsEnum.Express) {
      setProductPagePath(Pages.transferExpressPage)
    }
    if (currentProductId === ProductsEnum.ForNow) {
      setProductPagePath(Pages.transferTenMinPage)
    }
    if (currentProductId === ProductsEnum.RPI) {
      setProductPagePath(Pages.aboutRepeatPayments)
    }
  }, [currentProductId])

  useEffect(() => {
    setDefaultProductId(pageProductId)
  }, [location.pathname, pageProductId])

  useEffect(() => {
    if (!currencyIn) {
      dispatch(currenciesGet('in'))
    }
    if (!currencyOut) {
      dispatch(currenciesGet('out'))
    }
  }, [currencyIn, currencyOut, dispatch])

  useEffect(() => {
    if (isMounted.current === false) {
      isMounted.current = true
      if (currencyIn && currencyOut) {
        dispatch(productsGet(currencyIn?.[0]?.id, currencyOut?.[0]?.id))
      }
      dispatch(getCalculatorContent())
    }

    if (previousProductId !== defaultProductId) setValue('productId', defaultProductId)
    if (prevLang !== lang) dispatch(getCalculatorContent())

    trigger('transactionValue').then((res) => {
      if (res && currentProductId) {
        setLoading(true)
        calculatorPost(lang, currentProductId, payInAmount, setCalculatorData, setValue, setLoading, dispatch)
      }
    })
  }, [
    payInAmount,
    currentProductId,
    defaultProductId,
    previousProductId,
    trigger,
    dispatch,
    prevLang,
    lang,
    setValue,
    currencyIn,
    currencyOut
  ])

  const charge = calculatorData?.charge || 0
  const productList = content?.productLists

  useEffect(() => {
    setCurrentProductItem(
      productList?.find((product) => product?.productId === currentProductId)
      || products.find((product) => product.id === currentProductId)
    )
  }, [currentProductId, productList, products])

  const openCalculator = () => {
    const { current } = calculatorRef || {}
    window.scrollTo(0, current?.offsetTop)
  }

  const changeProduct = (type) => {
    let index = products.findIndex((product) => product.id === currentProductId)
    if (type === 'prev') {
      if (index !== 0) {
        index -= 1
      } else {
        index = products.length - 1
      }
    } else if (index !== products.length - 1) {
      index += 1
    } else {
      index = 0
    }
    setValue('productId', products[index]?.id)
  }

  const onSubmit = async () => {
    SendAnalyticsEvent(AnalyticsEventEnum.CLICK_MAKE_NEW_TRANSFER)
    if (!isLogin) {
      sessionStorage.setItem('calculatorData', { currentProduct: currentProductId, calculatorData })
      navigate(getPath(Pages.loginPage, lang))
    } else {
      navigate(getPath(currentProductId === ProductsEnum.RPI ? Pages.rpiAddPage : Pages.transferPage, lang))
    }
  }

  if (!content?.createdAt || !products?.[0]?.id) {
    return <div />
  }

  return (
    <div className="calculator mt-2 mt-md-0">
      <Form className="calculator-form" onSubmit={handleSubmit(onSubmit)}>
        <Card className="border-0 shadow" ref={calculatorRef}>
          <Card.Header className="bg-primary text-white px-3 pt-2 pb-1 fw-500">
            {/* Selected Product */}
            <Row className="flex-nowrap selected-product">
              <Col>
                <Row className="g-2">
                  {(currentProduct?.icon || currentProduct?.iconImage) && (
                    <Col xs="auto">
                      {currentProduct?.icon ? (
                        <i className="material-icons product-icon mt-2">{currentProduct.icon}</i>
                      ) : (
                        <img
                          className="product-icon-img"
                          src={config.url + currentProduct.iconImage.url}
                          alt={currentProduct.iconImage.alternativeText}
                        />
                      )}
                    </Col>
                  )}
                  <Col className="product-name">
                    <div className="fw-500">{currentProduct?.name}</div>
                    <div className="product-label font-sm text-light-85">
                      {currentProduct?.deliveryTIme}
                      <Link to={getPath(productPagePath)} className="text-light-85 ms-2">
                        <small>{content?.moreInfo}</small>
                      </Link>
                    </div>
                  </Col>
                </Row>
              </Col>
              <Col xs="auto text-end">
                <div className="product-label font-sm text-light-85">{content.fee}</div>
                <div className="product-charge font-sm fw-500">{currencyFormatterGBP(charge)}</div>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="px-lg-3">
            {/* Send from Input */}
            <Row className="g-0 input-wrapper border rounded position-relative">
              <Col>
                <Controller
                  control={control}
                  name="transactionValue"
                  defaultValue="1"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <div className="input-block">
                      <Form.Label
                        htmlFor="transactionValue"
                        className="small mb-0 mt-2 ms-2 ps-1 text-truncate text-muted"
                      >
                        {getInputLabel('transactionValue', 'calculator.inputs')}{' '}
                        <span className="fw-500 text-dark">{content?.fromCountryList?.[0]?.name}</span>
                      </Form.Label>
                      <Form.Control
                        id="transactionValue"
                        step="0.01"
                        required
                        type="number"
                        value={value || ''}
                        onBlur={onBlur}
                        onChange={(e) => {
                          onChange(e)
                          trigger(['transactionValue'])
                            .then((res) => {
                              if (res) {
                                setLoading(true)
                                calculatorPost(
                                  lang,
                                  currentProductId,
                                  e.target.value,
                                  setCalculatorData,
                                  setValue,
                                  setLoading,
                                  dispatch,
                                  false,
                                  navigate
                                )
                              }
                            })
                            .finally(() => setLoading(false))
                        }}
                      />
                    </div>
                  )}
                />
              </Col>
              <Col
                xs="auto"
                className="bg-gray d-flex flex-column align-items-center justify-content-center currency-wrapper"
              >
                <Flag country="GB" />
                <small className="fw-500">{CurrencyEnum.GBP}</small>
              </Col>
            </Row>
            {errors?.transactionValue?.message && (
              <Form.Text className="text-danger">{errors.transactionValue.message}</Form.Text>
            )}
            {/* summary */}
            <Row className="calculator-summary g-2 mt-2 align-items-center font-sm px-1">
              <Col>
                <span className="text-muted-light">{content.exchangeRate}</span>:
                <strong className="ms-1 text-nowrap">{formatExchangeRate(calculatorData.exchangeRate)}</strong>
              </Col>
              <Col className="text-sm-end">
                <span className="text-muted-light">{content.sum}</span>:
                <strong className="text-primary ms-1 text-nowrap">
                  {currencyFormatterGBP(parseFloat(calculatorData?.sendingAmount) + charge)}
                </strong>
              </Col>
            </Row>
            {/* Received from Input */}
            <Row className="mt-3 g-0 input-wrapper border rounded position-relative">
              <Col>
                <Controller
                  control={control}
                  name="payoutValue"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <div className="input-block">
                      <Form.Label htmlFor="payoutValue" className="small mb-0 mt-2 ms-2 ps-1 text-truncate text-muted">
                        {getInputLabel('payoutValue', 'calculator.inputs')}{' '}
                        <span className="fw-500 text-dark">{content?.receivedCountry}</span>
                      </Form.Label>
                      <Form.Control
                        id="payoutValue"
                        step="0.01"
                        required
                        type="number"
                        value={value || ''}
                        onBlur={onBlur}
                        onChange={(e) => {
                          onChange(e)
                          trigger(['payoutValue'])
                            .then((res) => {
                              if (res) {
                                setLoading(true)
                                calculatorPost(
                                  lang,
                                  currentProductId,
                                  e.target.value,
                                  setCalculatorData,
                                  setValue,
                                  setLoading,
                                  dispatch,
                                  true,
                                  navigate
                                )
                              }
                            })
                            .finally(() => setLoading(false))
                        }}
                      />
                    </div>
                  )}
                />
              </Col>
              <Col
                xs="auto"
                className="bg-gray d-flex flex-column align-items-center justify-content-center currency-wrapper"
              >
                <Flag country="PL" />
                <small className="fw-500">{CurrencyEnum.PLN}</small>
              </Col>
            </Row>
            {errors?.payoutValue?.message && (
              <Form.Text className="text-danger">{errors.payoutValue.message}</Form.Text>
            )}
            {/* Product Select */}
            <div className="mt-3 transfer-types">
              <div className="text-muted-light product-select-label mb-2">
                {getInputLabel('productId', 'calculator.inputs')}
              </div>
              <ProductPicker
                products={products}
                cmsProducts={productList}
                currentProductId={currentProductId}
                onSelect={(productId) => setValue('productId', productId)}
              />
            </div>
            <Button
              type="submit"
              variant="success"
              className="w-100 submit-btn mt-2 mb-0 text-white"
              disabled={loading}
            >
              {currentProductId === ProductsEnum.RPI ? content.rpiBtn : content.submitBtn}
            </Button>
          </Card.Body>
        </Card>
      </Form>
      <div className="calculator-popup-links d-lg-none d-flex">
        <Button
          variant="primary"
          className="rounded-0 d-flex flex-column align-items-center justify-content-center ps-2 pe-0 px-sm-3"
          onClick={() => changeProduct('prev')}
        >
          <i className="material-icons h1 m-0">arrow_back_ios</i>
        </Button>
        <Button
          variant="primary"
          className="px-2 py-1 d-flex align-items-center justify-content-between col rounded-0 text-start fw-500"
          onClick={() => {
            if (currentProductId === ProductsEnum.RPI) {
              navigate(getPath(Pages.repeatPayments, lang))
            } else {
              openCalculator()
            }
          }}
        >
          <span>
            <span className="d-block line-1">{currentProduct?.name}</span>
            <small>1 {CurrencyEnum.GBP} = </small>
            {loading ? (
              <Spinner size="sm" animation="border" variant="light" className="mb-n1" />
            ) : (
              `${calculatorData?.exchangeRate} ${CurrencyEnum.PLN}`
            )}
          </span>
          <small className="btn btn-sm btn-success ms-3 fw-500 line-1">{content?.calculateBtn}</small>
        </Button>
        <Button
          variant="primary"
          className="rounded-0 d-flex flex-column align-items-center justify-content-center px-1 px-sm-3"
          onClick={() => changeProduct()}
        >
          <i className="material-icons h1 m-0">arrow_forward_ios</i>
        </Button>
      </div>
    </div>
  )
}

Calculator.propTypes = {
  pageProductId: PropTypes.number
}

export default Calculator
